import { MixpanelEvents } from "@/constants/mixpanel-events";
import {
  aiChatModalOpenAtom,
  chatMessagesAtom,
  chatOriginAtom,
  chatSessionIdAtom,
  pendingChatContextAtom,
  streamInMessageIdAtom,
} from "@/stores/chat.store";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import { useSetAtom } from "jotai";
import { useCallback } from "react";
import {
  buildWeeklyBalanceChat,
  type WeeklyBalanceVariant,
} from "../utils/build-weekly-balance-chat";

/**
 * Opens the NutriCoach chat from the selected overview balance explainer.
 * Starts a fresh conversation seeded with a scripted exchange — a fixed user
 * question plus NutriCoach's explanation, which fake-streams in (see
 * `streamInMessageIdAtom`) — and stashes a context prefix so the agent has
 * continuity on the user's first real follow-up (see `pendingChatContextAtom`).
 */
export const useOpenWeeklyBalanceCoach = (variant: WeeklyBalanceVariant = "weekly-deficit") => {
  const setMessages = useSetAtom(chatMessagesAtom);
  const setSessionId = useSetAtom(chatSessionIdAtom);
  const setPendingContext = useSetAtom(pendingChatContextAtom);
  const setStreamInMessageId = useSetAtom(streamInMessageIdAtom);
  const setOrigin = useSetAtom(chatOriginAtom);
  const openAiChatModal = useSetAtom(aiChatModalOpenAtom);

  return useCallback(() => {
    const { userMessage, reply, contextPrefix } = buildWeeklyBalanceChat(variant);

    const replyId = crypto.randomUUID();
    setSessionId(crypto.randomUUID());
    setMessages([
      { id: crypto.randomUUID(), role: "user", text: userMessage },
      { id: replyId, role: "agent", text: reply, traceId: null },
    ]);
    setPendingContext(contextPrefix);
    // Fake-stream the reply in when the chat mounts; the user bubble shows in
    // full immediately above it (see streamInMessageIdAtom).
    setStreamInMessageId(replyId);
    // Tag as a coach conversation so the bottom-bar Coach button resumes it,
    // matching the "Adjust plan" entry point.
    setOrigin("coach");

    trackMixpanelEvent(MixpanelEvents.OVERVIEW_WEEKLY_BALANCE_EXPLAINER_TAPPED);

    openAiChatModal(true);
  }, [
    variant,
    setMessages,
    setSessionId,
    setPendingContext,
    setStreamInMessageId,
    setOrigin,
    openAiChatModal,
  ]);
};
